import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideAlignCenter,
  lucideAlignLeft,
  lucideAlignRight,
  lucideArrowDownToLine,
  lucideArrowUpToLine,
  lucideBold,
  lucideCheck,
  lucideChevronDown,
  lucideCircle,
  lucideCopy,
  lucideDiamond,
  lucideItalic,
  lucideMinus,
  lucidePlus,
  lucideSquare,
  lucideStar,
  lucideTrash2,
  lucideTriangle,
  lucideTriangleAlert,
} from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import {
  CatalogEntry,
  CatalogFont,
  DesignCatalog,
  DesignElement,
  ElementFont,
  ImageFit,
  ImageShape,
  TextAlign,
  VerticalAlign,
  VoucherType,
  ptToMm,
} from '../../../../services/voucher-design.service';
import { AlignMode, PageSize, READABLE_PT, estimateCapacity } from '../../editor-geometry';
import { PlaceholderMap, titleOf } from '../../shared/placeholders';
import { ALIGN_BUTTONS, ALIGN_ICONS } from '../selection-panel/selection-panel.component';
import { defaultFont } from '../../voucher-design-editor.store';
import { CompanyService } from '../../../../services/company.service';
import { BoxShape, SHAPE_CHOICES } from '../../shared/shapes';
import { ColorFieldComponent } from '../color-field/color-field.component';
import { PlaceholderFieldComponent } from '../placeholder-field/placeholder-field.component';

/** Position, size, font and text of the selected element. */
@Component({
  selector: 'app-element-inspector',
  standalone: true,
  imports: [
    ColorFieldComponent,
    PlaceholderFieldComponent,
    HlmDropdownMenuImports,
    NgIcon,
    HlmButtonImports,
    HlmCardImports,
    HlmInputImports,
    HlmLabelImports,
    HlmTooltipImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      ...ALIGN_ICONS,
      lucideAlignCenter,
      lucideAlignLeft,
      lucideAlignRight,
      lucideArrowDownToLine,
      lucideArrowUpToLine,
      lucideBold,
      lucideCheck,
      lucideChevronDown,
      lucideCircle,
      lucideCopy,
      lucideDiamond,
      lucideItalic,
      lucideMinus,
      lucidePlus,
      lucideSquare,
      lucideStar,
      lucideTrash2,
      lucideTriangle,
      lucideTriangleAlert,
    }),
  ],
  templateUrl: './element-inspector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ElementInspectorComponent {
  readonly element = input<DesignElement | null>(null);
  readonly entry = input<CatalogEntry | null>(null);
  readonly page = input<PageSize>({ width: 595, height: 842 });
  readonly catalog = input<DesignCatalog | null>(null);
  /** The catalog's placeholders by key: labels and samples for a text's content. */
  readonly placeholders = input<PlaceholderMap>(new Map());
  /** Product values are offered on product designs only. */
  readonly voucherType = input<VoucherType>('money');
  /** Where the element sits in the stacking order: first prints at the bottom. */
  readonly index = input(0);
  readonly count = input(1);
  /** The library picture of the selected `image` element: object URL and pixel size. */
  readonly picture = input<{ src: string; width: number; height: number } | null>(null);

  /** Not `change`: the native change events of the inputs inside bubble to the host
   * and would arrive on the same binding. */
  readonly patch = output<Partial<DesignElement>>();
  readonly remove = output<void>();
  /** The element once more, next to itself (⌘D). */
  readonly duplicate = output<void>();
  readonly moveBack = output<void>();
  readonly moveFront = output<void>();
  /** Open the image library to swap the picture of an `image` element. */
  readonly pickImage = output<void>();
  /** Align the element to the page. */
  readonly alignToPage = output<AlignMode>();
  /** From a part back to its whole group. */
  readonly selectGroup = output<void>();
  /** ⌘Z / ⌘⇧Z inside the content field: the editor's history. */
  readonly historyStep = output<'undo' | 'redo'>();

  readonly alignButtons = ALIGN_BUTTONS;

  readonly fonts = computed<CatalogFont[]>(() => this.catalog()?.fonts ?? []);
  cssOf(family: string): string {
    return this.fonts().find((f) => f.family === family)?.css ?? 'sans-serif';
  }

  readonly fontLabel = computed(
    () => this.fonts().find((f) => f.family === this.font().family)?.label ?? this.font().family,
  );
  readonly limits = computed(() => this.catalog()?.limits ?? null);
  readonly isText = computed(() => this.entry()?.render === 'text');
  readonly hasFont = this.isText;
  /** What the element is called: a text by its placeholder or its words. */
  readonly title = computed(() => {
    const element = this.element();
    const entry = this.entry() ?? undefined;
    return element ? titleOf(element, entry, this.placeholders()) : (entry?.label ?? '');
  });
  readonly hasColors = computed(() => !!this.entry()?.colors);
  private readonly companyService = inject(CompanyService);
  readonly isBrand = computed(() => this.entry()?.render === 'brand');
  readonly boxShapes = SHAPE_CHOICES;
  readonly boxShape = computed(() =>
    this.isBox() ? ((this.element()?.shape as BoxShape | undefined) ?? 'rect') : null,
  );
  readonly opacityPercent = computed(() => Math.round((this.element()?.opacity ?? 1) * 100));

  /** Switching to a line needs a thickness, switching away needs something to see. */
  setBoxShape(shape: BoxShape) {
    const e = this.element();
    if (!e) return;
    const patch: Partial<DesignElement> = { shape };
    if (shape === 'line' && !(e.border_width ?? 0)) patch.border_width = 2;
    if (shape !== 'line' && e.fill == null && !(e.border_width ?? 0)) patch.fill = '#d9d9d9';
    this.patch.emit(patch);
  }

  /** While dragging the slider the change is live; history merges it per element. */
  onOpacity(event: Event, _live: boolean) {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    if (Number.isFinite(value))
      this.patch.emit({ opacity: Math.min(1, Math.max(0.1, value / 100)) });
  }
  /** The logo's colour as shown: its own, else the shop's primary colour. */
  readonly brandColor = computed(
    () =>
      this.element()?.brand_color ??
      this.companyService.company()?.voucher_app_primary_color ??
      '#5271ff',
  );
  readonly properties = computed(() => new Set(this.entry()?.properties ?? []));
  readonly isImage = computed(() => this.entry()?.render === 'image');
  readonly isLibraryImage = computed(() => this.entry()?.image_source === 'library');
  readonly pictureRatio = computed(() => {
    const p = this.picture();
    return p && p.height > 0 ? p.width / p.height : null;
  });
  readonly isBox = computed(() => this.entry()?.render === 'box');
  readonly hasFrame = computed(() => this.properties().has('border'));
  readonly borderWidth = computed(() => this.element()?.border_width ?? 0);
  readonly borderColor = computed(() => this.element()?.border_color ?? '#ffffff');
  readonly borderRadius = computed(() => this.element()?.border_radius ?? 0);
  readonly fill = computed(() => this.element()?.fill ?? null);
  readonly fits: { value: ImageFit; label: string }[] = [
    { value: 'cover', label: 'Füllen' },
    { value: 'contain', label: 'Einpassen' },
  ];
  readonly shapes: { value: ImageShape; label: string }[] = [
    { value: 'rect', label: 'Rechteck' },
    { value: 'circle', label: 'Kreis' },
  ];
  readonly qrColor = computed(() => this.element()?.qr_color ?? '#000000');
  readonly qrBackground = computed(() => this.element()?.qr_background ?? '#ffffff');
  readonly font = computed<ElementFont>(() => this.element()?.font ?? defaultFont());
  /** How much text fits, and how small the longest allowed text would get. */
  readonly capacity = computed(() => {
    const element = this.element();
    return element
      ? estimateCapacity(element, this.entry() ?? undefined, this.placeholders())
      : null;
  });
  readonly readablePt = READABLE_PT;
  readonly aligns: { value: TextAlign; icon: string; label: string }[] = [
    { value: 'left', icon: 'lucideAlignLeft', label: 'Linksbündig' },
    { value: 'center', icon: 'lucideAlignCenter', label: 'Zentriert' },
    { value: 'right', icon: 'lucideAlignRight', label: 'Rechtsbündig' },
  ];
  readonly valigns: { value: VerticalAlign; label: string }[] = [
    { value: 'top', label: 'Oben' },
    { value: 'middle', label: 'Mitte' },
    { value: 'bottom', label: 'Unten' },
  ];

  mm(pt: number): number {
    return ptToMm(pt);
  }

  onNumber(field: 'x' | 'y' | 'width' | 'height', event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value)) this.patch.emit({ [field]: Math.round(value) });
  }

  onFont(patch: Partial<ElementFont>) {
    this.patch.emit({ font: { ...this.font(), ...patch } });
  }

  onFontSize(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value)) this.onFont({ size: value });
  }

  onValign(event: Event) {
    this.patch.emit({ valign: (event.target as HTMLSelectElement).value as VerticalAlign });
  }

  onQrQuietZone(event: Event) {
    this.patch.emit({ qr_quiet_zone: (event.target as HTMLInputElement).checked });
  }

  resetQrColors() {
    this.patch.emit({ qr_color: '#000000', qr_background: '#ffffff' });
  }

  onWrap(event: Event) {
    this.patch.emit({ wrap: (event.target as HTMLInputElement).checked });
  }

  onBorderWidth(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value) && value >= 0) this.patch.emit({ border_width: value });
  }

  onBorderRadius(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value) && value >= 0) this.patch.emit({ border_radius: value });
  }

  onFillNone(event: Event) {
    const none = (event.target as HTMLInputElement).checked;
    this.patch.emit({ fill: none ? null : '#ffffff' });
  }

  /** Give an image its picture's proportions, keeping the width. */
  applyAspect(ratio: number | null) {
    const element = this.element();
    if (!element || !ratio) return;
    this.patch.emit({ height: Math.round((element.width / ratio) * 2) / 2 });
  }
}
